'use client'

import React, { useState } from 'react'
import { useTheme } from '../theme/provider'

export interface TabItem {
  id: string
  label: string
  icon: React.ReactNode
  content: React.ReactNode
}

export interface SidebarTabsProps {
  tabs: TabItem[]
  defaultActiveTab?: string
  height?: string
  className?: string
  onChange?: (tabId: string) => void
}

/**
 * SidebarTabs - Real Madrid style tabs component
 * Based on the Real Madrid website sidebar tabs (Most Read, Recent, Popular)
 *
 * Features from Real Madrid:
 * - Three equal-width tabs with icons
 * - Gradient underline indicator on active tab
 * - Fixed height container (default 498px)
 * - Hover effects with color transitions
 * - Compact article list display
 * - Gradient separator lines between items
 */
export function SidebarTabs({
  tabs = [],
  defaultActiveTab,
  height = '498px',
  className = '',
  onChange,
}: SidebarTabsProps) {
  const [activeTab, setActiveTab] = useState(defaultActiveTab || tabs?.[0]?.id || '')
  const { theme } = useTheme()

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId)
    onChange?.(tabId)
  }

  if (!tabs || tabs.length === 0) {
    return (
      <div
        className={`p-4 rounded-2xl border text-center ${className}`}
        style={{
          height,
          backgroundColor: theme.colors.surface,
          borderColor: theme.colors.border,
          color: theme.colors.textSecondary,
        }}
      >
        No tabs available
      </div>
    )
  }

  const activeTabContent = tabs.find((tab) => tab.id === activeTab)

  return (
    <div
      className={`rounded-2xl shadow-lg border overflow-hidden flex flex-col ${className}`}
      style={{
        height,
        backgroundColor: theme.colors.surface,
        borderColor: theme.colors.border,
      }}
    >
      {/* Tabs (Real Madrid style: three equal-width buttons with gradient background) */}
      <div
        className="flex border-b flex-shrink-0"
        style={{
          background: `linear-gradient(to right, ${theme.colors.background}, ${theme.colors.surface})`,
          borderColor: theme.colors.border,
        }}
      >
        {tabs.map((tab, index) => {
          const isActive = activeTab === tab.id
          const isMiddle = index === 1

          return (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              className={`flex-1 px-3 py-3 text-[11px] font-bold transition-all duration-300 relative ${
                isMiddle ? 'border-x' : ''
              }`}
              style={{
                color: isActive ? theme.colors.primary : theme.colors.textSecondary,
                backgroundColor: isActive ? theme.colors.surface : 'transparent',
                borderColor: isMiddle ? theme.colors.border : 'transparent',
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.color = theme.colors.primary
                  e.currentTarget.style.backgroundColor = `${theme.colors.background}80`
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.color = theme.colors.textSecondary || '#666666'
                  e.currentTarget.style.backgroundColor = 'transparent'
                }
              }}
            >
              <div className="flex items-center justify-center gap-1.5">
                {/* Icon */}
                <span className="w-3.5 h-3.5">{tab.icon}</span>
                {/* Label */}
                <span>{tab.label}</span>
              </div>

              {/* Gradient underline indicator (Real Madrid signature style) */}
              {isActive && (
                <div
                  className="absolute bottom-0 left-0 right-0 h-0.5"
                  style={{
                    background: `linear-gradient(to right, ${theme.colors.primary}, ${theme.colors.primaryLight || theme.colors.primary}, ${theme.colors.primary})`,
                  }}
                />
              )}
            </button>
          )
        })}
      </div>

      {/* Content */}
      <div
        className="flex-1 overflow-hidden"
        style={{
          color: theme.colors.text,
        }}
      >
        <div className="h-full">{activeTabContent?.content}</div>
      </div>
    </div>
  )
}

/**
 * Helper SVG Icons (Real Madrid style)
 */
export const SidebarTabIcons = {
  Eye: (
    <svg fill="currentColor" viewBox="0 0 20 20">
      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
      <path
        fillRule="evenodd"
        d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
        clipRule="evenodd"
      />
    </svg>
  ),
  Clock: (
    <svg fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
        clipRule="evenodd"
      />
    </svg>
  ),
  Star: (
    <svg fill="currentColor" viewBox="0 0 20 20">
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
  ),
}
